import { combineReducers, Reducer } from '@reduxjs/toolkit';
import { loadingBarReducer } from 'react-redux-loading-bar';
import { loginReducer } from '../components/auth/components/login';
import { signupReducer } from '../components/auth/components/signup';
import resetPasswordReducer from '../components/auth/components/reset-password/reset-password-slice';
import { resetPasswordCallbackReducer } from '../components/auth/components/reset-password-callback';
import changePasswordReducer from '../components/auth/components/change-password/change-password-slice';
import setUserPasswordReducer from '../components/auth/components/change-password/set-user-password-slice';
import type SequenceReducer from '../components/sequence/sequence-slice';
import emailAccountReducer from '../components/settings/components/email-account/email-account-slice';
import scheduleReducer from '../components/settings/components/schedule/schedule-slice';
import safetySettingsReducer from '../components/settings/components/safety-settings/safety-settings-slice';
import customFieldReducer from '../components/settings/components/custom-fields/custom-field-slice';
import myProfileReducer from '../components/settings/components/my-profile/my-profile-slice';
import teamReducer from '../components/settings/components/team/team-slice';
import invitationSignupReducer from '../components/auth/components/invitation-signup/invitation-signup-slice';
import { reset } from './actions';
import type ProspectReducer from '../components/prospect/prospect-slice';
import customDomainReducer from '../components/settings/components/custom-domain/custom-domain-slice';
import adminSettingsReducer from '../components/settings/components/admin-settings/admin-settings-slice';
import homeReducer from '../components/home/home-slice';
import type ReportsReducer from '../components/reports/reports-slice';
import type TemplateReducer from '../components/templates/templates-slice';
import blacklistDomainReducer from '../components/settings/components/domain-blacklisting/domain-blacklisting-slice';
import type EmailModalReducer from '../components/sequence/shared/modals/email-modal/test-email/test-email-slice';
import { pageHeaderBannerAlertReducer } from '../shared/design-system/components/atoms/page-header';
import billingReducer from '../components/settings/components/billing-subscription/billing-subscription-slice';
import updatePreferredAppReducer from '../shared/components/connect-email-account-options/connect-email-account-slice';
import apiTokensReducer from '../components/api-keys/api-keys-slice';
import emailDailySendingLimitReducer from '../components/settings/components/email-daily-sending-limit/email-daily-sending-limit-slice';
import mailboxEmailsReducer from '../components/mailbox-emails/mailbox-emails-slice';
import emailWarmupReducer from '../components/email-warmup/email-warmup-slice';
import connectMailboxReducer from '../components/connect-mailbox/connect-mailbox-slice';
import accountDetailsReducer from '../components/auth/components/account-details/account-details-slice';
import type GrowthHubReducer from '../components/growth-hub/growth-hub-slice';
import { oauthReducer } from '../components/auth/components/oauth-buttons';
import billingAndSubscriptionsReducer from '../components/billing-and-subscriptions/billing-and-subscriptions-slice';

export const staticReducers = {
  loadingBar: loadingBarReducer,
  login: loginReducer,
  signup: signupReducer,
  oauth: oauthReducer,
  accountDetails: accountDetailsReducer,
  resetPassword: resetPasswordReducer,
  resetPasswordCallback: resetPasswordCallbackReducer,
  changePassword: changePasswordReducer,
  setUserPassword: setUserPasswordReducer,
  emailAccount: emailAccountReducer,
  schedule: scheduleReducer,
  safetySettings: safetySettingsReducer,
  customField: customFieldReducer,
  customDomain: customDomainReducer,
  myProfile: myProfileReducer,
  adminSettings: adminSettingsReducer,
  team: teamReducer,
  invitationSignup: invitationSignupReducer,
  home: homeReducer,
  pageHeader: pageHeaderBannerAlertReducer,
  blacklistDomains: blacklistDomainReducer,
  billingAndSubscription: billingReducer,
  updatePreferredApp: updatePreferredAppReducer,
  apiToken: apiTokensReducer,
  emailDailySendingLimit: emailDailySendingLimitReducer,
  mailboxEmails: mailboxEmailsReducer,
  emailWarmup: emailWarmupReducer,
  connectMailbox: connectMailboxReducer,
  billingAndSubscriptions: billingAndSubscriptionsReducer,
};

const staticReducer = combineReducers(staticReducers);

// sequence/prospect/emailModal are not part of the static reducer map: they're
// injected by `injectReducer` (see ./store) only once their owning lazy chunk
// loads, so their slice code doesn't ship in the main bundle. The types below
// are erased at build time (`import type`) — only the shapes are kept.
export type RootState = ReturnType<typeof staticReducer> & {
  sequence: ReturnType<typeof SequenceReducer>;
  prospect: ReturnType<typeof ProspectReducer>;
  emailModal: ReturnType<typeof EmailModalReducer>;
  growthHub: ReturnType<typeof GrowthHubReducer>;
  reports: ReturnType<typeof ReportsReducer>;
  templates: ReturnType<typeof TemplateReducer>;
};

export type InjectableReducerKey =
  | 'sequence'
  | 'prospect'
  | 'emailModal'
  | 'growthHub'
  | 'reports'
  | 'templates';
export type InjectedReducersMap = Partial<
  Record<InjectableReducerKey, Reducer>
>;

export const createRootReducer = (
  injectedReducers: InjectedReducersMap = {},
): Reducer<RootState> => {
  const combinedReducer = combineReducers({
    ...staticReducers,
    ...injectedReducers,
  }) as Reducer<RootState>;

  return (state, action) => {
    if (action.type === reset.type) {
      state = undefined;
    }
    return combinedReducer(state, action);
  };
};

export default createRootReducer();
